import { useEffect, useRef, useCallback } from "react";
import {
  fetchVapidPublicKey,
  registerPushSubscription,
  unregisterPushSubscription,
} from "../services/pushSubscriptionService";

/**
 * Converts a base64url VAPID public key string to a Uint8Array
 * required by pushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/**
 * usePushSubscription
 *
 * Registers the service worker, subscribes to push notifications,
 * and sends the subscription to the backend.
 *
 * Call `subscribeToPush(userId)` after notification permission is granted.
 * Call `unsubscribeFromPush()` on logout.
 */
export function usePushSubscription() {
  const swRegistrationRef = useRef(null);
  const currentSubscriptionRef = useRef(null);

  // Register the service worker once on mount
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        swRegistrationRef.current = registration;
        console.log("[PushSub] Service Worker registered:", registration.scope);

        // Listen for subscription rotation messages from SW
        navigator.serviceWorker.addEventListener("message", handleSwMessage);
      })
      .catch((err) =>
        console.warn("[PushSub] SW registration failed:", err.message),
      );

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleSwMessage);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSwMessage = useCallback((event) => {
    if (event.data?.type === "PUSH_SUBSCRIPTION_CHANGED") {
      // SW rotated the subscription — re-register with server
      const userId = localStorage.getItem("push_userId");
      if (userId && event.data.subscription) {
        registerPushSubscription(userId, {
          toJSON: () => event.data.subscription,
        }).catch((err) =>
          console.warn(
            "[PushSub] Re-register after rotation failed:",
            err.message,
          ),
        );
      }
    }
  }, []);

  /**
   * Subscribe this device to push notifications and register with backend.
   * Safe to call multiple times — will skip if already subscribed.
   */
  const subscribeToPush = useCallback(async (userId) => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.warn("[PushSub] Push API not supported in this browser");
      return;
    }
    if (Notification.permission !== "granted") return;

    try {
      // Ensure SW is ready
      const registration =
        swRegistrationRef.current || (await navigator.serviceWorker.ready);
      swRegistrationRef.current = registration;

      // Check for existing subscription first (avoid duplicate server calls)
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        const vapidPublicKey = await fetchVapidPublicKey();
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
        console.log("[PushSub] New push subscription created");
      } else {
        console.log("[PushSub] Reusing existing push subscription");
      }

      currentSubscriptionRef.current = subscription;
      localStorage.setItem("push_userId", userId);

      await registerPushSubscription(userId, subscription);
      console.log("[PushSub] Subscription registered with server ✅");
    } catch (err) {
      console.warn("[PushSub] subscribeToPush failed:", err.message);
    }
  }, []);

  /**
   * Unsubscribe this device from push notifications.
   * Call on logout or when permission is revoked.
   */
  const unsubscribeFromPush = useCallback(async () => {
    try {
      const registration =
        swRegistrationRef.current ||
        ("serviceWorker" in navigator && (await navigator.serviceWorker.ready));
      if (!registration) return;

      const subscription =
        currentSubscriptionRef.current ||
        (await registration.pushManager.getSubscription());
      if (!subscription) return;

      await unregisterPushSubscription(subscription.endpoint);
      await subscription.unsubscribe();
      currentSubscriptionRef.current = null;
      localStorage.removeItem("push_userId");
      console.log("[PushSub] Unsubscribed from push notifications");
    } catch (err) {
      console.warn("[PushSub] unsubscribeFromPush failed:", err.message);
    }
  }, []);

  return { subscribeToPush, unsubscribeFromPush };
}
